import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  Upload as UploadIcon,
  Sync as SyncIcon,
  Done as DoneIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { importService } from '../../../services/import.service';
import type { ImportProgress, ImportOptions, ImportJobStatus } from '../../../types/import.types';

interface ImportProgressStepProps {
  jobId: string;
  options: ImportOptions;
  onComplete: () => void;
}

interface ProgressStage {
  name: string;
  icon: React.ReactNode;
  status: 'pending' | 'active' | 'completed' | 'error';
  message?: string;
}

export const ImportProgressStep: React.FC<ImportProgressStepProps> = ({
  jobId,
  options,
  onComplete,
}) => {
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [stages, setStages] = useState<ProgressStage[]>([
    { name: 'Preparing import', icon: <UploadIcon />, status: 'pending' },
    { name: 'Validating records', icon: <SyncIcon />, status: 'pending' },
    { name: 'Importing leads', icon: <HourglassIcon />, status: 'pending' },
    { name: 'Import complete', icon: <DoneIcon />, status: 'pending' },
  ]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let isCompleted = false;

    const fetchProgress = async () => {
      try {
        const progressData = await importService.getImportStatus(jobId);
        setProgress(progressData);

        // Update stages based on progress
        updateStages(progressData);

        // Check if import is completed or failed
        if (
          progressData.status === 'completed' ||
          progressData.status === 'failed' ||
          progressData.status === 'cancelled'
        ) {
          isCompleted = true;
          if (intervalId) {
            clearInterval(intervalId);
          }
          
          // Wait a moment to show the final state
          setTimeout(() => {
            onComplete();
          }, 2000);
        }
      } catch (err: any) {
        console.error('Error fetching import progress:', err);
        setError(err.response?.data?.message || 'Failed to fetch import progress');
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    };

    // Initial fetch
    fetchProgress();

    // Poll for progress updates every 2 seconds
    intervalId = setInterval(() => {
      if (!isCompleted) {
        fetchProgress();
      }
    }, 2000);

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [jobId, onComplete]);

  const updateStages = (progressData: ImportProgress) => {
    const newStages: ProgressStage[] = [...stages];

    switch (progressData.status) {
      case 'uploaded':
      case 'parsing':
        newStages[0].status = 'active';
        break;
      case 'validating':
      case 'validated':
        newStages[0].status = 'completed';
        newStages[1].status = 'active';
        break;
      case 'approved':
      case 'importing':
        newStages[0].status = 'completed';
        newStages[1].status = 'completed';
        newStages[2].status = 'active';
        newStages[2].message = `${progressData.progress.processed} of ${progressData.progress.total} records processed`;
        break;
      case 'completed':
        newStages[0].status = 'completed';
        newStages[1].status = 'completed';
        newStages[2].status = 'completed';
        newStages[3].status = 'completed';
        break;
      case 'failed':
        const activeIndex = newStages.findIndex(s => s.status === 'active');
        if (activeIndex !== -1) {
          newStages[activeIndex].status = 'error';
          newStages[activeIndex].message = 'Import failed';
        }
        break;
    }

    setStages(newStages);
  };

  const getStatusColor = (status: ImportJobStatus): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'importing':
      case 'validating':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStageIcon = (stage: ProgressStage) => {
    switch (stage.status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'active':
        return stage.icon;
      default:
        return <Box sx={{ opacity: 0.3 }}>{stage.icon}</Box>;
    }
  };

  const calculateProgress = (): number => {
    if (!progress || progress.progress.total === 0) return 0;
    return (progress.progress.processed / progress.progress.total) * 100;
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this import?')) {
      setIsCancelling(true);
      try {
        await importService.cancelImport(jobId);
      } catch (err: any) {
        console.error('Error cancelling import:', err);
        alert('Failed to cancel import: ' + (err.response?.data?.message || err.message));
      } finally {
        setIsCancelling(false);
      }
    }
  };

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box display="flex" justifyContent="flex-end">
          <Button onClick={onComplete}>Close</Button>
        </Box>
      </Paper>
    );
  }

  if (!progress) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <LinearProgress sx={{ width: '100%' }} />
          <Typography variant="body2" color="text.secondary">
            Loading import status...
          </Typography>
        </Box>
      </Paper>
    );
  }

  const canCancel = progress.status === 'importing' || progress.status === 'validating';

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import Progress</Typography>
          <Box display="flex" gap={1} alignItems="center">
            <Chip
              label={progress.status.toUpperCase()}
              color={getStatusColor(progress.status)}
              size="small"
            />
            {canCancel && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={isCancelling}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Processing Records
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {progress.progress.processed} / {progress.progress.total}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={calculateProgress()}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Stage Progress */}
        <List sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
          {stages.map((stage, index) => (
            <React.Fragment key={stage.name}>
              <ListItem>
                <ListItemIcon>{getStageIcon(stage)}</ListItemIcon>
                <ListItemText
                  primary={stage.name}
                  secondary={stage.message}
                  primaryTypographyProps={{
                    fontWeight: stage.status === 'active' ? 600 : 400,
                    color: stage.status === 'error' ? 'error' : 'inherit',
                  }}
                />
              </ListItem>
              {index < stages.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>

        {/* Statistics */}
        <Box
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          gap={2}
          sx={{
            bgcolor: 'background.default',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box textAlign="center">
            <Typography variant="h4" color="success.main">
              {progress.progress.imported}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Imported
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h4" color="error.main">
              {progress.progress.failed}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Failed
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h4" color="text.secondary">
              {progress.progress.total - progress.progress.processed}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Remaining
            </Typography>
          </Box>
        </Box>

        {/* Error Messages */}
        {progress.errors && progress.errors.length > 0 && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Import Errors ({progress.errors.length}):
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              {progress.errors.slice(0, 5).map((err, idx) => (
                <li key={idx}>
                  <Typography variant="caption">
                    Row {err.row}: {err.error}
                  </Typography>
                </li>
              ))}
              {progress.errors.length > 5 && (
                <li>
                  <Typography variant="caption" fontStyle="italic">
                    ... and {progress.errors.length - 5} more errors
                  </Typography>
                </li>
              )}
            </Box>
          </Alert>
        )}

        {/* Completion Message */}
        {progress.status === 'completed' && (
          <Alert severity="success">
            <Typography variant="body2">
              Import completed successfully! {progress.progress.imported} leads imported.
            </Typography>
          </Alert>
        )}

        {/* Failed Message */}
        {progress.status === 'failed' && (
          <Alert severity="error">
            <Typography variant="body2">
              Import failed. Please check the errors and try again.
            </Typography>
          </Alert>
        )}

        {/* Cancelled Message */}
        {progress.status === 'cancelled' && (
          <Alert severity="warning">
            <Typography variant="body2">
              Import was cancelled. {progress.progress.imported} leads were imported before cancellation.
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
  );
};